import Swiper, {Autoplay, Navigation, EffectCreative, Pagination} from 'swiper';
import { swiperObserver } from '../../utils/swiper.utils.js';

let ourTeamSwiperWr = document.querySelectorAll('.our-team-swiper-wr');

// --- helper: throttle ---
function throttle(fn, wait = 200) {
  let last = 0;
  let timeout;

  return function (...args) {
    const now = Date.now();
    const remaining = wait - (now - last);

    if (remaining <= 0) {
      clearTimeout(timeout);
      timeout = null;
      last = now;
      fn.apply(this, args);
    } else if (!timeout) {
      timeout = setTimeout(() => {
        last = Date.now();
        timeout = null;
        fn.apply(this, args);
      }, remaining);
    }
  };
}

function shouldPauseAutoplayOnHover() {
  return window.innerWidth >= 1033;
}

function pauseSwiperAutoplay(swiper) {
  if (!swiper?.autoplay || !shouldPauseAutoplayOnHover()) return;

  if (typeof swiper.autoplay.pause === 'function') {
    swiper.autoplay.pause();
    return;
  }

  if (typeof swiper.autoplay.stop === 'function') {
    swiper.autoplay.stop();
  }
}

function resumeSwiperAutoplay(swiper) {
  if (!swiper?.autoplay) return;

  if (!shouldPauseAutoplayOnHover()) {
    if (!swiper.autoplay.running && typeof swiper.autoplay.start === 'function') {
      swiper.autoplay.start();
    }
    return;
  }

  if (typeof swiper.autoplay.resume === 'function') {
    swiper.autoplay.resume();
    return;
  }

  if (!swiper.autoplay.running && typeof swiper.autoplay.start === 'function') {
    swiper.autoplay.start();
  }
}

function bindAutoplayHoverPause(swiper, targets) {
  if (!swiper?.autoplay || !targets?.length) return;

  const hoveredTargets = new Set();

  const handleEnter = (event) => {
    if (!shouldPauseAutoplayOnHover()) return;

    hoveredTargets.add(event.currentTarget);
    pauseSwiperAutoplay(swiper);
  };

  const handleLeave = (event) => {
    hoveredTargets.delete(event.currentTarget);

    if (!hoveredTargets.size) {
      resumeSwiperAutoplay(swiper);
    }
  };

  targets.forEach((target) => {
    if (!target) return;

    target.addEventListener('mouseenter', handleEnter);
    target.addEventListener('mouseleave', handleLeave);
  });

  const syncAutoplayOnResize = throttle(() => {
    if (!shouldPauseAutoplayOnHover()) {
      hoveredTargets.clear();
      resumeSwiperAutoplay(swiper);
      return;
    }

    if (hoveredTargets.size) {
      pauseSwiperAutoplay(swiper);
    }
  }, 120);

  window.addEventListener('resize', syncAutoplayOnResize);
}

// --- helper: equalize .our-team-card__content min-height on screens < 480 ---
function equalizeContentHeights(root) {
  if (!root) return;

  const contents = root.querySelectorAll('.our-team-card__content');
  if (!contents.length) return;

  contents.forEach((c) => { c.style.minHeight = ''; });

  if (window.innerWidth >= 480) return;

  let max = 0;
  contents.forEach((c) => {
    const h = c.getBoundingClientRect().height;
    if (h > max) max = h;
  });

  if (max > 0) {
    contents.forEach((c) => { c.style.minHeight = max + 'px'; });
  }
}

// --- helper: update height by active slide ---
function updateSwiperHeight(swiper) {
  if (!swiper || !swiper.el) return;

  const activeSlide = swiper.el.querySelector('.swiper-slide-active');
  if (!activeSlide) return;

  const wrapper = activeSlide.querySelector('.our-team-card__wrapper');
  if (!wrapper) return;

  const height = wrapper.scrollHeight;

  swiper.el.style.height = height + 'px';
}

ourTeamSwiperWr.forEach((el) => {
  if (!el) return;

  const parentSec = el.closest('.our-team-sec');

  let swiperEl = el.querySelector('.swiper');
  let nextEl = el.querySelector('.swiper-button-next');
  let prevEl = el.querySelector('.swiper-button-prev');
  let paginationEl = parentSec.querySelector('.swiper-pagination');

  let swiperSlides = el.querySelectorAll('.swiper-slide');

  let swiper = new Swiper(swiperEl, {
    modules: [Autoplay, Navigation, EffectCreative, Pagination],
    observer: true,
    observeParents: true,
    watchSlidesProgress: true,
    effect: 'creative',
    spaceBetween: 16,
    slidesPerView: 1,
    speed: 500,
    loop: swiperSlides.length >= 3 ? true : false,
    loopedSlides: swiperSlides.length >= 3 ? 3 : 0,
    threshold: 10,
    pagination: {
      el: paginationEl,
      clickable: true,
    },
    creativeEffect: {
      perspective: false,
      limitProgress: 6,
      progressMultiplier: 1,
      prev: {
        translate: ['-150%', 0, -1],
        opacity: 0,
      },
      next: {
        translate: [window.innerWidth > 640 ? 'calc(100% - 99px)' : 'calc(100% + 16px)', 0, 0],
        scale: 1,
      },
    },

    navigation: {
      nextEl: nextEl,
      prevEl: prevEl,
    },

    breakpoints: {
      992: {
        slidesPerView: 1,
        spaceBetween: 20,
      },
      1200: {
        slidesPerView: 1,
        spaceBetween: 26,
      }
    },

    autoplay: {
      delay: 4500,
      disableOnInteraction: false,
      pauseOnMouseEnter: window.innerWidth >= 1033,
    },

    on: {
      init(swiper) {
        setTimeout(() => {
          requestAnimationFrame(() => {
            equalizeContentHeights(el);
            updateSwiperHeight(swiper);
          });
        }, 120);
      },

      slideChangeTransitionEnd(swiper) {
        setTimeout(() => {
          requestAnimationFrame(() => updateSwiperHeight(swiper));
        }, 120);
      }
    }
  });

  swiperObserver(swiper);

  bindAutoplayHoverPause(swiper, [
    swiperEl,
    ...Array.from(el.querySelectorAll('.our-team-card')),
    nextEl,
    prevEl,
    paginationEl,
  ]);

  // resize (throttled)
  const resizeHandler = throttle(() => {
    equalizeContentHeights(el);
    updateSwiperHeight(swiper);
  }, 200);

  window.addEventListener('resize', resizeHandler);

  if (window.ResizeObserver) {
    const ro = new ResizeObserver(resizeHandler);
    const wrappers = el.querySelectorAll('.our-team-card__wrapper');
    wrappers.forEach(w => ro.observe(w));
  }

  if (window.IntersectionObserver) {
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        equalizeContentHeights(el);
        updateSwiperHeight(swiper);
      }
    });
    io.observe(el);
  }
});
